import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  HStack,
  Spinner,
  Text,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import {
  reportNoteTabKeysForReportType,
  REPORT_NOTE_TAB_LABELS,
  REPORT_NOTE_TAB_SECTION_HEADINGS,
  type ReportNoteTabKey,
} from "../../constants/reportNotes";
import { listReportNotesGrouped, NotesApiError } from "../../services/notesApi";
import type { Note, ReportNotesGroupedResponse } from "../../types/notes";
import Card from "../Card/Card";
import CardBody from "../Card/CardBody";
import CardHeader from "../Card/CardHeader";
import { reportDetailSectionCardProps } from "../../views/Dashboard/Reports/components/ReportsCardChrome";
import { NotesListEditor } from "./NotesListEditor";
import { ReportTabNotesAccordion } from "./ReportTabNotesAccordion";
import { useNotesListControlled } from "./useNotesList";

// Chakra polymorphic props can trigger "union too complex" IDE diagnostics in large TSX trees.
const VStackAny = VStack as any;
const HStackAny = HStack as any;
const SpinnerAny = Spinner as any;
const TextAny = Text as any;
const AlertAny = Alert as any;
const AlertIconAny = AlertIcon as any;
const AlertDescriptionAny = AlertDescription as any;

export function ReportNotesHubPanel(props: {
  reportId: string;
  reportType?: string;
  /** Reload grouped notes when the Notes tab becomes active. */
  isActive?: boolean;
  subtleText?: string;
  textColor?: string;
  borderColor?: string;
}): JSX.Element {
  const defaultBorder = useColorModeValue("gray.200", "gray.600");
  const defaultSubtle = useColorModeValue("gray.500", "gray.400");
  const defaultText = useColorModeValue("gray.700", "gray.200");
  const borderColor = props.borderColor ?? defaultBorder;
  const subtleText = props.subtleText ?? defaultSubtle;
  const textColor = props.textColor ?? defaultText;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [grouped, setGrouped] = useState<ReportNotesGroupedResponse | null>(null);
  const [masterNotes, setMasterNotes] = useState<Note[]>([]);
  const [tabNotes, setTabNotes] = useState<Record<string, Note[]>>({});

  const reloadGrouped = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listReportNotesGrouped(props.reportId);
      setGrouped(data);
      setMasterNotes(data.master);
      setTabNotes(data.tabs ?? {});
    } catch (e) {
      const message =
        e instanceof NotesApiError
          ? e.detail || e.message
          : e instanceof Error
            ? e.message
            : "Failed to load notes.";
      setError(message);
      setGrouped(null);
      setMasterNotes([]);
      setTabNotes({});
    } finally {
      setLoading(false);
    }
  }, [props.reportId]);

  useEffect(() => {
    if (props.isActive === false) return;
    void reloadGrouped();
  }, [reloadGrouped, props.isActive]);

  const showHubLoading = Boolean(props.isActive !== false && loading);

  const masterScope = useMemo(
    () => ({ entityType: "report" as const, entityId: props.reportId, scope: "master" as const }),
    [props.reportId]
  );

  const masterEditor = useNotesListControlled(masterNotes, setMasterNotes, masterScope);

  const tabKeys = useMemo(
    () => [...reportNoteTabKeysForReportType(props.reportType)],
    [props.reportType]
  );

  const tabLabelFor = useCallback(
    (tabKey: string) =>
      REPORT_NOTE_TAB_SECTION_HEADINGS[tabKey as ReportNoteTabKey] ??
      grouped?.tab_labels?.[tabKey] ??
      REPORT_NOTE_TAB_LABELS[tabKey as ReportNoteTabKey] ??
      tabKey,
    [grouped?.tab_labels]
  );

  return (
    <VStackAny align="stretch" spacing={6} w="100%">
      {showHubLoading ? (
        <Card {...reportDetailSectionCardProps}>
          <CardBody>
            <HStackAny py={4}>
              <SpinnerAny size="sm" />
              <TextAny fontSize="sm" color={subtleText}>
                Loading notes…
              </TextAny>
            </HStackAny>
          </CardBody>
        </Card>
      ) : error ? (
        <AlertAny status="error" borderRadius="6px">
          <AlertIconAny />
          <AlertDescriptionAny>{error}</AlertDescriptionAny>
        </AlertAny>
      ) : (
        <>
          <Card {...reportDetailSectionCardProps} w="100%">
            <CardHeader pb={{ base: 2, md: 3 }} flexDirection="column" alignItems="stretch">
              <Box flex="1" minW={0}>
                <TextAny
                  fontSize={{ base: "2xl", md: "3xl" }}
                  color="#38BBD1"
                  fontWeight="normal"
                  fontFamily="'Urbanist', sans-serif"
                  lineHeight="1.15"
                >
                  Master notes
                </TextAny>
                <TextAny fontSize="sm" color={subtleText} mt={1}>
                  Whole-report context, conclusions, and follow-ups.
                </TextAny>
              </Box>
            </CardHeader>
            <CardBody pt={0}>
              <NotesListEditor
                composerVariant="shadcn"
                composerLabel="Master notes"
                emptyLabel="No master notes yet."
                notes={masterEditor.notes}
                draft={masterEditor.draft}
                onDraftChange={masterEditor.setDraft}
                editingId={masterEditor.editingId}
                editingBody={masterEditor.editingBody}
                onEditingBodyChange={masterEditor.setEditingBody}
                saving={masterEditor.saving}
                canSubmit={masterEditor.canSubmit}
                onCreate={() => void masterEditor.onCreate()}
                onStartEdit={masterEditor.startEdit}
                onCancelEdit={masterEditor.cancelEdit}
                onSaveEdit={() => void masterEditor.saveEdit()}
                onDelete={(id) => void masterEditor.onDelete(id)}
                borderColor={borderColor}
                subtleText={subtleText}
                textColor={textColor}
              />
            </CardBody>
          </Card>

          <Card {...reportDetailSectionCardProps} w="100%">
            <CardHeader pb={{ base: 2, md: 3 }} flexDirection="column" alignItems="stretch">
              <Box flex="1" minW={0}>
                <TextAny
                  fontSize={{ base: "2xl", md: "3xl" }}
                  color="#38BBD1"
                  fontWeight="normal"
                  fontFamily="'Urbanist', sans-serif"
                  lineHeight="1.15"
                >
                  Notes by tab
                </TextAny>
                <TextAny fontSize="sm" color={subtleText} mt={1}>
                  Notes from each report tab, collected here.
                </TextAny>
              </Box>
            </CardHeader>
            <CardBody pt={0}>
              <ReportTabNotesAccordion
                reportId={props.reportId}
                tabKeys={tabKeys}
                tabLabelFor={tabLabelFor}
                tabNotes={tabNotes}
                onTabNotesChange={(tabKey, next) =>
                  setTabNotes((prev) => ({
                    ...prev,
                    [tabKey]: next,
                  }))
                }
                borderColor={borderColor}
                subtleText={subtleText}
                textColor={textColor}
              />
            </CardBody>
          </Card>
        </>
      )}
    </VStackAny>
  );
}
